import { useMemo } from "react";
import { EmailDraftI, EmailSequenceI } from "./interfaces";
import { DayJs } from "shared/lib/helpers/date";

import { SCHEDULER_TIMEZONES } from "./content/v2/sections/preview-settings-section/scheduler/select-timezone";

export const selectorEmailSequenceOrderByEmailId = (
  drafts: Record<string, EmailDraftI>,
  darftId: string,
  emailId: string
) => {
  const draft = drafts?.[darftId as string];

  if (!draft) return undefined;

  return draft?.emails?.findIndex((e) => e.email_id === emailId);
};

export const useSelectorEmailAIGenerationOptionalParams = (
  drafts: Record<string, EmailDraftI>,
  darftId: string,
  emailId: string
) =>
  useMemo(() => {
    const sequenceOrder = selectorEmailSequenceOrderByEmailId(
      drafts,
      darftId as string,
      emailId as string
    );

    return sequenceOrder
      ? {
          sequenceOrder,
        }
      : undefined;
  }, [drafts, darftId, emailId]);

/**
 * Check if the email is the primary email
 *
 * If there is no sequence_id or no draft, it is the primary email
 *
 * If there is a sequence_id, it is the primary email if the email is the first one
 *
 * @param drafts
 * @param darftId
 * @param emailId
 * @returns
 */
export const useSelectorIsEmailPrimary = (
  drafts: Record<string, EmailDraftI>,
  darftId?: string,
  emailId?: string
) =>
  useMemo(() => {
    const draft = drafts?.[darftId as string];

    if (!draft) return true;

    if (!draft?.sequence_id) return true;

    return draft?.emails?.findIndex((e) => e.email_id === emailId) === 0;
  }, [drafts, darftId, emailId]);

export const useSelectorIsFollowUpEmail = (
  drafts: Record<string, EmailDraftI>,
  darftId?: string,
  emailId?: string
) =>
  useMemo(() => {
    const draft = drafts?.[darftId as string];

    if (!draft || !draft?.sequence_id) return false;

    return (draft?.emails?.findIndex((e) => e.email_id === emailId) || 0) > 0;
  }, [drafts, darftId, emailId]);

/**
 * Convert the followup setting to a date
 *
 * @param days - "7"
 * @param time - "9:00 AM"
 * @param timezone - "EST"
 * @param prevDate - "2025-04-24T09:00:00.000Z" - previous date is date of previous email that was or has to be sent
 * @returns
 */
export const selectorFollowupSettingToDate = (
  days: string,
  time: string,
  timezone: string,
  prevDate?: string
) => {
  const date = prevDate ? DayJs(prevDate) : DayJs();

  return date
    .tz(SCHEDULER_TIMEZONES.find((tz) => tz.abbr === timezone)?.tzCode)
    .add(parseInt(days), "days")
    .set(
      "hour",
      parseInt(time.split(":")[0]) +
        (time.includes("PM") && time.split(":")[0] !== "12" ? 12 : 0)
    )
    .set("minute", parseInt(time.split(" ")[0].split(":")[1]))
    .format();
};

/**
 * Convert a date to the followup setting
 *
 * @param date - "2025-04-24T09:00:00.000Z"
 * @param prevDate - "2025-04-24T09:00:00.000Z" - previous date is date of previous email that was or has to be sent
 * @returns
 */
export const selectorDateToFollowupSetting = (
  date: string,
  prevDate?: string
) => {
  //TODO get local timezone if other is not available
  // Get timezone
  const timezone = SCHEDULER_TIMEZONES.find((tz) => {
    const dateParts = date.split("-");
    const currentTimzoneOffset = `-${dateParts[dateParts.length - 1]}`;

    return tz.utc === currentTimzoneOffset;
  });

  // We want to keep the time as is, but change the timezone
  const dateWithoutTimezone = date.substring(0, date.length - 6);
  const dayjsDate = DayJs(dateWithoutTimezone).tz(timezone?.tzCode, true);

  const previousDate = prevDate ? DayJs(prevDate) : DayJs();
  const now = previousDate.tz(timezone?.tzCode, true);

  // Get days difference
  // Date created by now (DayJs()) needs + 1 as the diff is not inclusive
  const days = (dayjsDate.diff(now, "days") + (prevDate ? 0 : 1)).toString();

  // Get time in AM/PM format
  let hours = dayjsDate.hour();
  const minutes = dayjsDate.minute();
  const period = hours >= 12 ? "PM" : "AM";

  // Convert to 12 hour format
  if (hours > 12) {
    hours -= 12;
  } else if (hours === 0) {
    hours = 12;
  }

  const time = `${hours}:${minutes.toString().padStart(2, "0")} ${period}`;

  return {
    days,
    time,
    timezone: timezone?.tzCode,
  };
};

export const selectorEmailsSequenceDaysDiff = (emails?: EmailSequenceI) => {
  if (!emails) return [];

  const emailsDaysDiff = emails?.map((email, i) => {
    if (i == 0 || !email?.scheduled_at) return 0;

    const prevDate = i > 1 ? emails?.[i - 1]?.scheduled_at : undefined;
    return parseInt(
      selectorDateToFollowupSetting(email?.scheduled_at as string, prevDate)
        .days
    );
  });

  return emailsDaysDiff;
};
